/**
 * The icons the controls draw: chevrons, check, minus, close, external, info, warning, search and
 * plus. Each is a 20-unit outline in `currentColor` with a 1.5px stroke, so it takes the colour of
 * the text beside it and reads as one family.
 *
 * An icon is decoration by default and hidden from assistive technology, because the control around
 * it carries the name. Give it a `label` only when it stands alone and means something, such as a
 * warning mark beside a value, and it becomes an image with that name.
 *
 * The size follows the density theme (`size.icon`: 20px comfortable, 16px compact). The stroke
 * does not scale with it, so a 16px icon keeps the same line weight as the text beside it.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
export interface IconProps {
  /** Names the icon for a screen reader. Leave it out when a control or text beside it says it. */
  readonly label?: string;
}
function Svg({
  label,
  children
}: IconProps & {
  readonly children: ReactNode;
}) {
  const named = label === undefined ? {
    "aria-hidden": true
  } : {
    role: "img",
    "aria-label": label
  };
  return <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" focusable="false" {...named} className="x1rg5ohu x2lah0s x1z0q0p5 xq9ob8z x1pv9cyz">
      {children}
    </svg>;
}

/* Every path keeps its stroke at 1.5px whatever size the icon is drawn. */
function Path({
  d
}: {
  readonly d: string;
}) {
  return <path d={d} vectorEffect="non-scaling-stroke" />;
}
export function ChevronDownIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M5 7.5l5 5 5-5" />
    </Svg>;
}
export function ChevronRightIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M7.5 5l5 5-5 5" />
    </Svg>;
}
export function ChevronLeftIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M12.5 5l-5 5 5 5" />
    </Svg>;
}
export function CheckIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M4.5 10.5l3.5 3.5 7.5-8" />
    </Svg>;
}
export function MinusIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M5 10h10" />
    </Svg>;
}
export function PlusIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M10 5v10M5 10h10" />
    </Svg>;
}
export function CloseIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M5.5 5.5l9 9M14.5 5.5l-9 9" />
    </Svg>;
}
export function ExternalIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M8.5 4.5H5.5a1 1 0 00-1 1v9a1 1 0 001 1h9a1 1 0 001-1v-3M11.5 4.5h4v4M15.5 4.5L9 11" />
    </Svg>;
}
export function CopyIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M7.5 7.5h7a1 1 0 011 1v7a1 1 0 01-1 1h-7a1 1 0 01-1-1v-7a1 1 0 011-1zM4.5 12.5v-7a1 1 0 011-1h7" />
    </Svg>;
}
export function InfoIcon(props: IconProps) {
  return <Svg {...props}>
      <circle cx="10" cy="10" r="7" vectorEffect="non-scaling-stroke" />
      <Path d="M10 9v4.5M10 6.5v.01" />
    </Svg>;
}
export function WarningIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M10 3.5l7 12.5H3L10 3.5zM10 8.5v3.5M10 14v.01" />
    </Svg>;
}
export function SearchIcon(props: IconProps) {
  return <Svg {...props}>
      <circle cx="9" cy="9" r="5" vectorEffect="non-scaling-stroke" />
      <Path d="M12.75 12.75L16 16" />
    </Svg>;
}
export function MoreIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M5 10v.01M10 10v.01M15 10v.01" />
    </Svg>;
}

/*
  The navigation's icons, one for every item in the sidebar so the icon rail never falls back to a
  letter. They draw on the same 20-unit grid and keep the same 1.5px stroke as the icons above.
*/

export function HomeIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M3 9.5L10 3.5l7 6M5 8.5V16h10V8.5M8.5 16v-4h3v4" />
    </Svg>;
}
export function PeopleIcon(props: IconProps) {
  return <Svg {...props}>
      <circle cx="7" cy="7" r="2.5" vectorEffect="non-scaling-stroke" />
      <Path d="M2.5 16c0-2.8 2-4.5 4.5-4.5s4.5 1.7 4.5 4.5" />
      <circle cx="14" cy="7.5" r="2" vectorEffect="non-scaling-stroke" />
      <Path d="M14 12c2 .1 3.5 1.5 3.5 4" />
    </Svg>;
}
export function ClipboardIcon(props: IconProps) {
  return <Svg {...props}>
      <rect x="4.5" y="4" width="11" height="13" rx="1.5" vectorEffect="non-scaling-stroke" />
      <Path d="M8 4V3h4v1M7.5 9h5M7.5 12h5" />
    </Svg>;
}
export function PackageIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M3 6.5L10 3l7 3.5v7L10 17l-7-3.5zM3 6.5l7 3.5 7-3.5M10 10v7" />
    </Svg>;
}
export function DocumentIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M5 3h7l3 3v11H5zM12 3v3h3M7.5 10h5M7.5 13h5" />
    </Svg>;
}
export function HeartIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M10 16.5S3.5 12.5 3.5 8a3.2 3.2 0 0 1 6.5-1 3.2 3.2 0 0 1 6.5 1c0 4.5-6.5 8.5-6.5 8.5z" />
    </Svg>;
}
export function LockIcon(props: IconProps) {
  return <Svg {...props}>
      <rect x="4.5" y="9" width="11" height="8" rx="1.5" vectorEffect="non-scaling-stroke" />
      <Path d="M7 9V6.5a3 3 0 0 1 6 0V9" />
    </Svg>;
}
export function BuildingIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M4 17V4h8v13M12 8h4v9M4 17h12M7 7h2M7 10h2M7 13h2" />
    </Svg>;
}
export function ChartIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M4 16V9M9 16V4M14 16v-5M3 16.5h14" />
    </Svg>;
}
export function LogIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M5 4h10M5 8h10M5 12h6M5 16h4" />
    </Svg>;
}
export function FlaskIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M8 3h4M9 3v5l-4.5 7.5a1 1 0 0 0 .9 1.5h9.2a1 1 0 0 0 .9-1.5L11 8V3M6.5 12.5h7" />
    </Svg>;
}
export function SlidersIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M4 6h12M4 14h12" />
      <circle cx="7" cy="6" r="1.5" vectorEffect="non-scaling-stroke" />
      <circle cx="13" cy="14" r="1.5" vectorEffect="non-scaling-stroke" />
    </Svg>;
}
export function BoltIcon(props: IconProps) {
  return <Svg {...props}>
      <Path d="M11 3L5 11h4l-1 6 6-8h-4z" />
    </Svg>;
}
export function CardIcon(props: IconProps) {
  return <Svg {...props}>
      <rect x="3" y="5" width="14" height="10" rx="2" vectorEffect="non-scaling-stroke" />
      <Path d="M3 9h14M6 12.5h3" />
    </Svg>;
}